import { useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// ─── Timezone data ──────────────────────────────────────────────────────────

interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

interface TimezoneGroup {
  label: string;
  options: TimezoneOption[];
}

const TIMEZONE_GROUPS: TimezoneGroup[] = [
  {
    label: 'Центральная Азия',
    options: [
      { value: 'Asia/Tashkent', label: 'Ташкент', offset: 'UTC+5' },
      { value: 'Asia/Samarkand', label: 'Самарканд', offset: 'UTC+5' },
      { value: 'Asia/Almaty', label: 'Алматы', offset: 'UTC+6' },
      { value: 'Asia/Bishkek', label: 'Бишкек', offset: 'UTC+6' },
      { value: 'Asia/Dushanbe', label: 'Душанбе', offset: 'UTC+5' },
      { value: 'Asia/Ashgabat', label: 'Ашхабад', offset: 'UTC+5' },
    ],
  },
  {
    label: 'Россия',
    options: [
      { value: 'Europe/Kaliningrad', label: 'Калининград', offset: 'UTC+2' },
      { value: 'Europe/Moscow', label: 'Москва', offset: 'UTC+3' },
      { value: 'Europe/Samara', label: 'Самара', offset: 'UTC+4' },
      { value: 'Asia/Yekaterinburg', label: 'Екатеринбург', offset: 'UTC+5' },
      { value: 'Asia/Omsk', label: 'Омск', offset: 'UTC+6' },
      { value: 'Asia/Krasnoyarsk', label: 'Красноярск', offset: 'UTC+7' },
      { value: 'Asia/Irkutsk', label: 'Иркутск', offset: 'UTC+8' },
      { value: 'Asia/Yakutsk', label: 'Якутск', offset: 'UTC+9' },
      { value: 'Asia/Vladivostok', label: 'Владивосток', offset: 'UTC+10' },
      { value: 'Asia/Kamchatka', label: 'Камчатка', offset: 'UTC+12' },
    ],
  },
  {
    label: 'Европа',
    options: [
      { value: 'Europe/London', label: 'Лондон', offset: 'UTC+0' },
      { value: 'Europe/Berlin', label: 'Берлин', offset: 'UTC+1' },
      { value: 'Europe/Paris', label: 'Париж', offset: 'UTC+1' },
      { value: 'Europe/Rome', label: 'Рим', offset: 'UTC+1' },
      { value: 'Europe/Madrid', label: 'Мадрид', offset: 'UTC+1' },
      { value: 'Europe/Warsaw', label: 'Варшава', offset: 'UTC+1' },
      { value: 'Europe/Kyiv', label: 'Киев', offset: 'UTC+2' },
      { value: 'Europe/Bucharest', label: 'Бухарест', offset: 'UTC+2' },
      { value: 'Europe/Helsinki', label: 'Хельсинки', offset: 'UTC+2' },
      { value: 'Europe/Athens', label: 'Афины', offset: 'UTC+2' },
      { value: 'Europe/Istanbul', label: 'Стамбул', offset: 'UTC+3' },
    ],
  },
  {
    label: 'Ближний Восток',
    options: [
      { value: 'Asia/Dubai', label: 'Дубай', offset: 'UTC+4' },
      { value: 'Asia/Riyadh', label: 'Эр-Рияд', offset: 'UTC+3' },
      { value: 'Asia/Tehran', label: 'Тегеран', offset: 'UTC+3:30' },
      { value: 'Asia/Jerusalem', label: 'Иерусалим', offset: 'UTC+2' },
      { value: 'Asia/Baku', label: 'Баку', offset: 'UTC+4' },
      { value: 'Asia/Tbilisi', label: 'Тбилиси', offset: 'UTC+4' },
      { value: 'Asia/Yerevan', label: 'Ереван', offset: 'UTC+4' },
    ],
  },
  {
    label: 'Азия и Тихий океан',
    options: [
      { value: 'Asia/Kolkata', label: 'Дели / Мумбаи', offset: 'UTC+5:30' },
      { value: 'Asia/Dhaka', label: 'Дакка', offset: 'UTC+6' },
      { value: 'Asia/Bangkok', label: 'Бангкок', offset: 'UTC+7' },
      { value: 'Asia/Jakarta', label: 'Джакарта', offset: 'UTC+7' },
      { value: 'Asia/Singapore', label: 'Сингапур', offset: 'UTC+8' },
      { value: 'Asia/Shanghai', label: 'Пекин / Шанхай', offset: 'UTC+8' },
      { value: 'Asia/Hong_Kong', label: 'Гонконг', offset: 'UTC+8' },
      { value: 'Asia/Seoul', label: 'Сеул', offset: 'UTC+9' },
      { value: 'Asia/Tokyo', label: 'Токио', offset: 'UTC+9' },
      { value: 'Australia/Sydney', label: 'Сидней', offset: 'UTC+10' },
    ],
  },
  {
    label: 'Америка',
    options: [
      { value: 'America/New_York', label: 'Нью-Йорк', offset: 'UTC-5' },
      { value: 'America/Chicago', label: 'Чикаго', offset: 'UTC-6' },
      { value: 'America/Denver', label: 'Денвер', offset: 'UTC-7' },
      { value: 'America/Los_Angeles', label: 'Лос-Анджелес', offset: 'UTC-8' },
      { value: 'America/Sao_Paulo', label: 'Сан-Паулу', offset: 'UTC-3' },
      { value: 'America/Argentina/Buenos_Aires', label: 'Буэнос-Айрес', offset: 'UTC-3' },
    ],
  },
];

function findTimezone(value: string): TimezoneOption | undefined {
  for (const group of TIMEZONE_GROUPS) {
    const found = group.options.find((tz) => tz.value === value);
    if (found) return found;
  }
  return undefined;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface TimezonePickerProps {
  value?: string;
  onChange: (timezone: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function TimezonePicker({
  value,
  onChange,
  placeholder = 'Выберите часовой пояс',
  disabled = false,
  className,
}: TimezonePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = value ? findTimezone(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <Globe className="h-4 w-4 shrink-0" />
            {selected
              ? `${selected.label} (${selected.offset})`
              : value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Поиск города..." />
          <CommandList>
            <CommandEmpty>Не найдено</CommandEmpty>
            {TIMEZONE_GROUPS.map((group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.options.map((tz) => (
                  <CommandItem
                    key={tz.value}
                    value={`${tz.label} ${tz.value} ${tz.offset}`}
                    onSelect={() => {
                      onChange(tz.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === tz.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="flex-1 truncate">{tz.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{tz.offset}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
